import React, { useState, useEffect, useRef } from 'react';
import '../../stylesheets/UserManagement.css';
import { useNavigate } from 'react-router-dom';
import FileDropDown from './FileDropDown';
import EditUser from './EditUser';
import CustomAlert from './CustomAlert';
import Toolbar from '../Toolbar';
import Sidebar from '../Sidebar';
import AddUser from "./AddUser";
import { UserManagementAPI } from '../../axios';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const ManageUsers = () => {
  const navigate = useNavigate();
  const [addNewUser, setAddNewUser] = useState(false);
  const [roleNames, setRoleNames] = useState([]);
  const [users, setUsers] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [option, setOption] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const usersPerPage = 5;
  const pagesToShow = 5;
  const dropdownRefs = useRef({});

  useEffect(() => {
    UserManagementAPI.fetch_roles()
      .then((res) => {
        if (res.code === 200) {
          const roles = res.data.roles.map(role => role.name);
          setRoleNames(roles);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    UserManagementAPI.fetch_admin_users(selectedUser)
      .then((res) => {
        if (res.code === 200) {
          setUsers(res.data.adminUsers);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [selectedUser]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownElements = Object.values(dropdownRefs.current);
      if (dropdownElements.every(ref => ref && !ref.contains(event.target))) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = (userId) => {
    setSelectedUser(userId);
    setShowAlert(true);
    setOption('Delete');
  };

  const confirmDelete = () => {
    setShowAlert(false);
    UserManagementAPI.delete_admin_user(selectedUser)
      .then((res) => {
        if (res.code === 200) {
          console.log(res);
        }
      })
      .catch((err) => {
        console.log(err);
      });
    setSelectedUser(null);
  };

  const cancelDelete = () => {
    setShowAlert(false);
    setSelectedUser(null);
  };

  const handleEdit = (userId) => {
    setOption('Edit');
    setShowAlert(true);
    setSelectedUser(userId);
  };

  const handleAddUserSubmit = (newUser) => {
    setUsers(prevUsers => [...prevUsers, newUser]);
    setAddNewUser(false);
  };

  const handleEditUser = () => {
    setShowAlert(false);
    setOption(null);
  };

  const closeDialog = () => {
    setAddNewUser(false);
  };

  const openDialog = () => {
    setAddNewUser(true);
  };

  const handleDropdownToggle = (userId) => {
    setOpenDropdownId(openDropdownId === userId ? null : userId);
  };

  // Pagination logic
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);

  const totalPages = Math.ceil(users.length / usersPerPage);
  const startPage = Math.max(1, currentPage - Math.floor(pagesToShow / 2));
  const endPage = Math.min(totalPages, startPage + pagesToShow - 1);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const handlePrevSet = () => setCurrentPage((prev) => Math.max(1, prev - pagesToShow));
  const handleNextSet = () => setCurrentPage((prev) => Math.min(totalPages, prev + pagesToShow));

  const permissions = JSON.parse(localStorage.getItem("adminData"))?.superAdmin ? ["Can edit users", "Can delete users"] : JSON.parse(localStorage.getItem("adminData"))?.role?.permissions;

  if (!permissions?.includes("Can view users") && !JSON.parse(localStorage.getItem("adminData")).superAdmin) {
    return null;
  }

  return (
    <div className="main-container">
      <Toolbar />
      <Sidebar />
      <div className="content-container">
        <div className="main-content">
          <div className="management-header">
            <h3>User Management</h3>
            <div className="buttons">
              <button className="add-user" onClick={openDialog}>Add New User</button>
            </div>
          </div>
          <div className="manage-headings">
            <span
              style={{
                textDecoration: 'underline',
                textDecorationColor: '#8F9BC5',
                textDecorationThickness: '1.5px',
                textUnderlineOffset: '4px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              onClick={() => {
                navigate('/manage-users')
              }}
            >
              Manage Users
            </span>
            <span
              style={{
                textDecoration: 'none',
                textDecorationColor: 'transparent',
                textDecorationThickness: 'auto',
                textUnderlineOffset: '0px',
                fontWeight: '400',
                cursor: 'pointer',
              }}
              onClick={() => {
                navigate('/manage-roles')
              }}
            >
              Manage Roles
            </span>
          </div>
          <div style={{ height: '325px', marginTop: '30px' }}>
            <div className='manage-users'>
              <span>Name</span>
              <span>Email</span>
              <span>Role</span>
              <span>Location</span>
              <span>Action</span>
            </div>
            {currentUsers.map(user => (
              <div className='manage-users' key={user._id}>
                <span>{user.name ? user.name : 'N/A'}</span>
                <span>{user.email ? user.email : 'N/A'}</span>
                <span>{user.role ? user.role.name : 'N/A'}</span>
                <span>{user.location.join(', ')}</span>
                {["Can edit users", "Can delete users"].some(permission => permissions?.includes(permission)) ? (
                  <FileDropDown
                    userId={user._id}
                    onDelete={handleDelete}
                    onEdit={handleEdit}
                    isOpen={openDropdownId === user._id}
                    onToggle={() => handleDropdownToggle(user._id)}
                    ref={(el) => (dropdownRefs.current[user._id] = el)}
                  />
                ) : 'N/A'}
              </div>
            ))}
            <div className='pagination'>
              {startPage > 1 && (
                <button onClick={handlePrevSet} style={{ borderRadius: '3px', display: 'flex' }}>
                  <ArrowBackIosNewIcon />
                </button>
              )}
              {Array.from({ length: endPage - startPage + 1 }, (_, i) => (
                <button
                  key={startPage + i}
                  onClick={() => paginate(startPage + i)}
                  className={currentPage === startPage + i ? 'active' : ''}
                >
                  {startPage + i}
                </button>
              ))}
              {endPage < totalPages && (
                <button onClick={handleNextSet} style={{ borderRadius: '3px', display: 'flex' }}>
                  <ArrowForwardIosIcon />
                </button>
              )}
            </div>

            {showAlert && option === 'Edit' && (
              <EditUser
                userId={selectedUser}
                onCancel={cancelDelete}
                onSubmit={handleEditUser}
              />
            )}
            {showAlert && option === 'Delete' && (
              <CustomAlert
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
              />
            )}
          </div>
        </div>
      </div>
      {addNewUser && (
        <AddUser
          onCancel={closeDialog}
          onSubmit={handleAddUserSubmit}
          roleNames={roleNames}
        />
      )}
    </div>
  );
};

export default ManageUsers;
